import { useState } from "react";
import { toast } from "sonner";
import { Heart, Send, Share2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const Footer = ({ content }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "", subject: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { ...form, message: form.subject ? `[${form.subject}] ${form.message}` : form.message };
      await api.post("/contact", payload);
      toast.success("Messaggio inviato! L'autrice ti risponderà presto.");
      setForm({ name: "", email: "", message: "", subject: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: content.title, text: `${content.title} – ${content.subtitle}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiato! Condividilo con amici e famiglie.");
      }
    } catch {
      /* condivisione annullata */
    }
  };

  return (
    <footer id="contatti" className="bg-olive text-white" data-testid="site-footer">
      <div className="mx-auto grid max-w-6xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2">
        <div className="space-y-5">
          <span className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-terracotta text-white">
              <Heart className="h-4 w-4 fill-current" />
            </span>
            <span className="font-display text-lg font-bold">MetodoEmo</span>
          </span>
          <p className="max-w-md text-sm leading-relaxed text-white/75 sm:text-base">
            {content.title} di {content.author}. Disponibile in copertina flessibile su Amazon.it a {content.price}, con spedizione rapida Prime.
          </p>
          <button
            type="button"
            onClick={share}
            data-testid="share-page-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-white hover:text-olive"
          >
            <Share2 className="h-4 w-4" /> Condividi questa pagina
          </button>
          <p className="pt-4 text-xs text-white/50">
            © {new Date().getFullYear()} {content.author}. Tutti i diritti riservati. Sito promozionale indipendente: gli acquisti avvengono su Amazon.it.
          </p>
        </div>

        <div className="rounded-2xl bg-white/10 p-6 sm:p-8" data-testid="contact-form-box">
          <h3 className="font-display text-lg font-bold sm:text-xl">Scrivi all'autrice</h3>
          <p className="mt-1 text-sm text-white/70">Domande, presentazioni nelle scuole o semplicemente un saluto.</p>
          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="contact-form">
            <input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Il tuo nome"
              data-testid="contact-name-input"
              className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder-white/50 focus:border-amazon-orange focus:outline-none"
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="La tua email"
              data-testid="contact-email-input"
              className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder-white/50 focus:border-amazon-orange focus:outline-none"
            />
            <select
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              data-testid="contact-subject-select"
              className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white focus:border-amazon-orange focus:outline-none [&>option]:text-stone-800"
            >
              <option value="">Di cosa vuoi parlarci?</option>
              <option value="Percorsi per scuole o associazioni">Percorsi per scuole o associazioni</option>
              <option value="Libro Io Emozione">Il libro Io Emozione</option>
              <option value="Altro">Altro</option>
            </select>
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="Il tuo messaggio..."
              data-testid="contact-message-input"
              className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder-white/50 focus:border-amazon-orange focus:outline-none"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="inline-flex items-center gap-2 rounded-full bg-amazon-orange px-6 py-2.5 font-display text-sm font-bold text-amazon-navy transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              <Send className="h-4 w-4" /> {sending ? "Invio in corso..." : "Invia messaggio"}
            </button>
          </form>
        </div>
      </div>
    </footer>
  );
};
